import { BOOKING_CHRISTCHURCH } from "./booking";
import { CONCERNS, CONCERN_NAV_LABEL } from "./concerns";

export const SITE_NAME = "Silk Clinical Aesthetics";
export const SITE_URL = "https://silkclinical.co.nz";
export const SITE_DESCRIPTION =
  "Expert cosmetic injectables by registered nurses, and advanced clinical skin treatments, at a nurse-led clinic in Christchurch, New Zealand.";

export const BUSINESS = {
  name: "Silk Clinical Aesthetics",
  address: "66 Victoria Street, Christchurch Central, Christchurch 8013, New Zealand",
  streetAddress: "66 Victoria Street",
  locality: "Christchurch Central",
  region: "Canterbury",
  postalCode: "8013",
  country: "NZ",
  phone: "03 365 5774",
  phoneInternational: "+6433655774",
  email: "bookings@silkclinical.co.nz",
  instagram: "https://instagram.com/silk.clinical",
  facebook: "https://facebook.com/silk.clinical",
  lat: -43.5321,
  lng: 172.6362,
} as const;

/**
 * The Auckland pop-up venue, resolved 2026-08-04.
 *
 * Not a second clinic and not a second business. Silk rents a room at the same
 * Grey Lynn address for one clinic a month, so this address is stable and
 * publishable, but there are no opening hours to publish: the clinic is only
 * there on announced dates. Schema therefore emits an address and NO
 * `openingHoursSpecification`, and links back to the Christchurch node as
 * `parentOrganization`.
 *
 * Injectables only. The Volnewmer RF and Ultraformer HIFU platforms do not
 * travel, so anyone wanting those is a Christchurch booking. Everything offered
 * in Auckland is needle work, which means the registered-nurse claim is
 * accurate there without qualification (unlike the Christchurch menu, where
 * non-needle treatments may be performed by a beauty specialist).
 */
export const AUCKLAND_VENUE = {
  name: "Silk Clinical Aesthetics Auckland",
  streetAddress: "562 Richmond Road",
  locality: "Grey Lynn",
  region: "Auckland",
  // 1021, confirmed September 2026. Live copy and the Fresha listing both
  // carried 1011, which is Auckland Central: Grey Lynn and Richmond Road sit
  // in 1021, as does the building's own venue listing. This feeds the
  // LocalBusiness address in schema, so the wrong value was a machine-readable
  // error, not just a typo.
  postalCode: "1021",
  country: "NZ",
  freshaUrl:
    "https://www.fresha.com/a/silk-clinical-aesthetics-auckland-auckland-562-richmond-road-xo8hiibr",
} as const;

/**
 * Sitewide booking CTA target. Now the Christchurch-pinned booking screen
 * rather than Fresha's location-agnostic `all-offer` catalogue, which forced
 * every visitor to re-pick the venue (fix plan P0-3). Route-specific targets,
 * including the `/auckland` exception, live in `lib/booking.ts` — use
 * `bookingUrlFor(pathname)` when a call site knows its route.
 */
export const FRESHA_BOOKING_URL = BOOKING_CHRISTCHURCH;
export const FRESHA_GIFT_VOUCHER_URL =
  "https://www.fresha.com/book-now/silk-clinical-aesthetics-d1yzxk56/gift-cards?share=true&pId=2669966";
/** Public Fresha venue profile: full review listing lives here. */
export const FRESHA_PROFILE_URL =
  "https://www.fresha.com/a/silk-clinical-aesthetics-christchurch-christchurch-66-victoria-street-christchurch-central-city-arbk4o12";

/**
 * True Fresha aggregate, verified against the live profile.
 * `count` is the verified Fresha total, confirmed by Matt on 7 October 2026. Update
 * both `count` and `countDisplay` on the monthly featured-review refresh.
 * Never derive an aggregate from the curated set; this is the real,
 * unmodified figure (Fair Trading Act, change list reviews section).
 */
export const FRESHA_REVIEWS = {
  rating: "5.0",
  count: 379,
  countDisplay: "379",
  // Month the aggregate was last checked against the live Fresha profile.
  // Update this single constant on each monthly refresh; the /reviews page
  // freshness line renders from it.
  countVerified: "October 2026",
} as const;

/**
 * Google Business Profile share link (resolves to the knowledge panel with
 * the review listing).
 */
export const GOOGLE_REVIEWS_URL = "https://share.google/p8LsvA4PIabAFZbPP";

/**
 * Google aggregate shown next to the Fresha one. Entered manually from the
 * Google Business Profile (Google blocks automated reads, so this cannot
 * be script-verified): 4.8 from 175 reviews, confirmed by Matt
 * 7 October 2026. Re-confirm against the live listing on each monthly
 * review refresh, alongside FRESHA_REVIEWS. `countDisplay` is the exact
 * figure, matching the Fresha treatment: Matt's call, 2 September 2026, is
 * that both platform claims read as current numbers rather than a rounded
 * "150+" that undersells the real total. It follows that both must actually
 * be refreshed monthly, since a specific number goes stale in a way a "+"
 * figure does not.
 */
export const GOOGLE_REVIEWS_DISPLAY = {
  rating: "4.8",
  count: 175,
  countDisplay: "175",
  // Month the aggregate was last checked against the live Google listing.
  countVerified: "October 2026",
} as const;

/**
 * Weight-stability guidance for the device consult path (execution pack item
 * 5). Approved by Shontelle Prasad RN, 2 September 2026.
 *
 * Held as one constant so the identical approved sentence appears on every
 * device surface. The pack was explicit that this wording could not be
 * published without clinical sign-off; do not edit it without her.
 */
export const DEVICE_WEIGHT_STABILITY_NOTE =
  "If your weight is still changing, we will usually suggest waiting: rapid weight loss can deflate the midface and increase laxity, which changes what the device would be treating.";

/**
 * Location-scope line for the homepage visit block (execution pack item 6).
 *
 * The homepage must not imply that a visitor can book anywhere except
 * Christchurch. The Auckland pop-up is real and runs monthly, but it has no
 * standing hours and no open booking outside its announced dates, so it is
 * described here rather than offered. Replace this line only when a dated
 * Auckland clinic is confirmed AND the CTA can land on that exact date path.
 */
export const BOOKING_CHRISTCHURCH_DISPLAY_NOTE =
  "Current online booking is for our Christchurch clinic. Auckland pop-up dates are announced separately, once the date and its booking link are confirmed.";

/**
 * Opening hours, synchronised with the Fresha profile (verified July 2026).
 * Keep in sync with Fresha and the Google Business Profile whenever hours
 * change (change list item D / checklist row 19).
 */
export const OPENING_HOURS_DISPLAY = [
  { days: "Monday to Wednesday", hours: "9:30am to 2:30pm" },
  { days: "Thursday", hours: "9:30am to 7:00pm" },
  { days: "Friday", hours: "9:30am to 2:30pm" },
  { days: "Saturday", hours: "9:00am to 3:00pm" },
  { days: "Sunday", hours: "Closed" },
] as const;

export const NAV_ITEMS = [
  { label: "About", href: "/about" },
  {
    label: "Treatments",
    href: "/treatments",
    menu: "treatments",
    children: [
      {
        category: "Non-Surgical Skin Tightening & Lifting",
        items: [
          { label: "The Silk Protocol", href: "/treatments/silk-protocol", featured: true },
          { label: "Volnewmer RF Skin Tightening", href: "/treatments/volnewmer-rf-skin-tightening", featured: true },
          { label: "Ultraformer MPT HIFU", href: "/treatments/ultraformer-mpt-hifu", featured: true },
        ],
      },
      {
        category: "Cosmetic Injectables",
        items: [
          { label: "Anti-Wrinkle Injections", href: "/treatments/anti-wrinkle-injections" },
          { label: "Dermal Fillers", href: "/treatments/dermal-fillers" },
          { label: "Lip Filler", href: "/treatments/lip-filler" },
          { label: "Dermal Fillers for Men", href: "/treatments/dermal-fillers-for-men" },
          { label: "Skin Hydration Injections", href: "/treatments/skin-hydration-injections" },
        ],
      },
      {
        category: "Thread Treatments",
        items: [
          { label: "PDO Mono Threads", href: "/treatments/pdo-mono-threads" },
          { label: "Lifting Threads", href: "/treatments/lifting-threads" },
        ],
      },
      {
        category: "Skin Rejuvenation",
        items: [
          { label: "Microneedling", href: "/treatments/microneedling" },
          { label: "PRP Vampire Facial", href: "/treatments/prp-vampire-facial" },
          { label: "PRP for Hair Loss", href: "/treatments/prp-hair-loss" },
          { label: "Pro Dermal Active Treatments", href: "/treatments/skin-peels" },
          { label: "Microdermabrasion & Sonophoresis", href: "/treatments/microdermabrasion-sonophoresis" },
          { label: "Fat Dissolver", href: "/treatments/fat-dissolver" },
        ],
      },
      {
        category: "Packages",
        items: [
          { label: "Combined Treatment Plans", href: "/treatments/combined-treatment-plans" },
        ],
      },
      // Local landing pages are deliberately NOT in the header nav (owner
      // decision 2026-07-29): they stay linked from the footer Locations
      // block and the sitemap, which keeps them crawlable without loading
      // the treatments menu with location entries.
    ],
  },
  {
    label: CONCERN_NAV_LABEL,
    href: "/concerns",
    menu: "concerns",
    // Built from the by-need taxonomy in concerns.ts. Flagship concerns
    // need no special casing: the dropdown is a flat, single-column list
    // in the source order (flagship-first ordering lives per-concern).
    concerns: CONCERNS.map((concern) => ({
      label: concern.name,
      href: `/concerns/${concern.slug}`,
    })),
  },
  { label: "Pricing", href: "/pricing" },
] as const;

/**
 * STANDING RULE: customer reviews are never edited, condensed, or re-voiced.
 * Quotes below are verbatim as the customer wrote them (source:
 * src/lib/scraped-copy/homepage.json and pricing.json). Brand voice rules
 * (no exclamation marks, no em dashes) do NOT apply to customer quotes;
 * editing a review risks breaching the Fair Trading Act. Light trimming with
 * ellipses is acceptable only where it does not change meaning.
 */
// voice-pass-ignore-start (verbatim customer quotes)
export const TESTIMONIALS = [
  {
    quote:
      "I can't praise Shontelle enough! The combination of her expertise and the incredibly friendly atmosphere makes for an exceptional experience. Shontelle's warmth and welcoming vibe added a personal touch that set this visit apart. For top-notch service and a genuinely friendly environment, I wholeheartedly recommend her services.",
    author: "Makyla B.",
  },
  {
    quote:
      "What sets Shontelle apart is her patient care—she listens attentively and understands my skin concerns even better than I do. The clinic itself is beautiful and serene, adding to the overall experience. I leave feeling rejuvenated and utterly satisfied with the exceptional level of care I receive with every appointment. For anyone seeking a top-notch injectables or beauty experience, I wholeheartedly recommend Silk Clinical and am grateful to Shontelle for her outstanding service!",
    author: "Jasmine T.",
  },
] as const;
// voice-pass-ignore-end
